// src/hooks/useProperties.ts
import {useState, useCallback, useMemo} from "react";
import {
  useGetPropertiesQuery,
  useGetPropertyByIdQuery,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useDeletePropertyMutation,
  useBulkUpdatePropertiesMutation,
  useBulkDeletePropertiesMutation,
  useActivatePropertyMutation,
  useDeactivatePropertyMutation,
  useGetPropertyStatsQuery,
  useCreatePropertyWithPhotosMutation,
} from "../store/api/propertyApi";
import {
  useUploadPropertyPhotosMutation,
  useDeletePropertyPhotoMutation,
  useSetPrimaryPhotoMutation,
  useReorderPropertyPhotosMutation,
} from "../store/api/propertyPhotoApi";
import {useToast} from "./common/useToast";
import {useConfirm} from "./common/useConfirm";
import type {
  PropertyFilterDto,
  CreatePropertyDto,
  UpdatePropertyDto,
  BulkUpdatePropertyDto,
} from "../types/property";
import {photoService} from "../services/photoService";

export const useProperties = (filters: PropertyFilterDto = {}) => {
  const {success, error} = useToast();
  const {confirm} = useConfirm();

  // Query hooks
  const {
    data: propertiesResponse,
    isLoading,
    isFetching,
    error: fetchError,
    refetch,
  } = useGetPropertiesQuery(filters);

  const {data: statsData, isLoading: statsLoading} = useGetPropertyStatsQuery();

  // Mutation hooks

  const [createProperty, {isLoading: isCreating}] = useCreatePropertyMutation();
  const [createPropertyWithPhotos, {isLoading: isCreatingWithPhotos}] =
    useCreatePropertyWithPhotosMutation();
  const [updateProperty, {isLoading: isUpdating}] = useUpdatePropertyMutation();
  const [deleteProperty, {isLoading: isDeleting}] = useDeletePropertyMutation();
  const [bulkUpdate, {isLoading: isBulkUpdating}] =
    useBulkUpdatePropertiesMutation();
  const [bulkDelete, {isLoading: isBulkDeleting}] =
    useBulkDeletePropertiesMutation();
  const [activateProperty] = useActivatePropertyMutation();
  const [deactivateProperty] = useDeactivatePropertyMutation();

  // Photo mutations
  // Photo mutations
  const [uploadPhotosMutation] = useUploadPropertyPhotosMutation();
  const [deletePhotoMutation] = useDeletePropertyPhotoMutation();
  const [setPrimaryMutation] = useSetPrimaryPhotoMutation();
  const [reorderPhotosMutation] = useReorderPropertyPhotosMutation();

  // Memoized values
  const properties = useMemo(
    () => propertiesResponse?.data?.data || [],
    [propertiesResponse]
  );
  const pagination = useMemo(
    () => propertiesResponse?.data?.pagination,
    [propertiesResponse]
  );
  const stats = useMemo(
    () => propertiesResponse?.data?.stats || statsData,
    [propertiesResponse, statsData]
  );

  // Handlers
  const handleCreateWithPhotos = useCallback(
    async (data: CreatePropertyDto, files: File[] = []) => {
      try {
        console.log("🚀 Creating property with photos:", {
          data,
          fileCount: files.length,
        });

        const result = await createPropertyWithPhotos({data, files}).unwrap();

        success(
          "Success",
          `Property created successfully${
            files.length > 0 ? ` with ${files.length} photos` : ""
          }`
        );
        console.log("✅ Property created:", result);

        return result;
      } catch (err) {
        console.error("❌ Failed to create property:", err);
        error("Error", "Failed to create property");
        return null;
      }
    },
    [createPropertyWithPhotos, success, error]
  );

  const handleCreate = useCallback(
    async (data: CreatePropertyDto) => {
      try {
        const result = await createProperty(data).unwrap();
        success("Success", "Property created successfully");
        return result;
      } catch (err) {
        error("Error", "Failed to create property");
        return null;
      }
    },
    [createProperty, success, error]
  );

  const handleUpdate = useCallback(
    async (id: number, data: UpdatePropertyDto) => {
      try {
        await updateProperty({id, data}).unwrap();
        success("Success", "Property updated successfully");
        return true;
      } catch (err) {
        error("Error", "Failed to update property");
        return false;
      }
    },
    [updateProperty, success, error]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      const confirmed = await confirm({
        title: "Delete Property",
        message:
          "Are you sure you want to delete this property? This action cannot be undone.",
        variant: "danger",
        confirmText: "Delete",
      });

      if (!confirmed) return false;

      try {
        await deleteProperty(id).unwrap();
        success("Success", "Property deleted successfully");
        return true;
      } catch (err) {
        error("Error", "Failed to delete property");
        return false;
      }
    },
    [deleteProperty, confirm, success, error]
  );

  const handleBulkUpdate = useCallback(
    async (data: BulkUpdatePropertyDto) => {
      const confirmed = await confirm({
        title: "Bulk Update Properties",
        message: `Are you sure you want to update ${data.propertyIds.length} properties?`,
        variant: "warning",
        confirmText: "Update",
      });

      if (!confirmed) return false;

      try {
        await bulkUpdate(data).unwrap();
        success(
          "Success",
          `${data.propertyIds.length} properties updated successfully`
        );
        return true;
      } catch (err) {
        error("Error", "Failed to update properties");
        return false;
      }
    },
    [bulkUpdate, confirm, success, error]
  );

  const handleBulkDelete = useCallback(
    async (propertyIds: number[]) => {
      const confirmed = await confirm({
        title: "Delete Properties",
        message: `Are you sure you want to delete ${propertyIds.length} properties? This action cannot be undone.`,
        variant: "danger",
        confirmText: "Delete",
      });

      if (!confirmed) return false;

      try {
        await bulkDelete({propertyIds}).unwrap();
        success(
          "Success",
          `${propertyIds.length} properties deleted successfully`
        );
        return true;
      } catch (err) {
        error("Error", "Failed to delete properties");
        return false;
      }
    },
    [bulkDelete, confirm, success, error]
  );

  const handleStatusChange = useCallback(
    async (id: number, status: "active" | "inactive") => {
      try {
        if (status === "active") {
          await activateProperty(id).unwrap();
        } else {
          await deactivateProperty(id).unwrap();
        }
        success(
          "Success",
          `Property ${
            status === "active" ? "activated" : "deactivated"
          } successfully`
        );
        return true;
      } catch (err) {
        error(
          "Error",
          `Failed to ${
            status === "active" ? "activate" : "deactivate"
          } property`
        );
        return false;
      }
    },
    [activateProperty, deactivateProperty, success, error]
  );

  /**
   * Upload photos to a property
   */
  const handleUploadPhotos = async (
    propertyId: number,
    files: File[],
    options: {isPrimary?: boolean; sortOrder?: number} = {}
  ) => {
    console.log("🚀 Starting photo upload...");
    console.log("📋 Upload details:", {
      propertyId,
      fileCount: files.length,
      files: files.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
      })),
      options,
    });

    try {
      const result = await photoService.uploadPhotos(
        propertyId,
        files,
        options
      );
      console.log("✅ Upload completed successfully");
      return result;
    } catch (error) {
      console.error("❌ Upload failed in hook:", error);
      throw error;
    }
  };

  /**
   * Delete a photo
   */
  const handleDeletePhoto = async (photoId: number) => {
    console.log("🗑️ Deleting photo:", photoId);

    try {
      const result = await photoService.deletePhoto(photoId);
      console.log("✅ Photo deleted successfully");
      return result;
    } catch (error) {
      console.error("❌ Delete failed:", error);
      throw error;
    }
  };

  /**
   * Set a photo as primary
   */
  const handleSetPrimaryPhoto = async (propertyId: number, photoId: number) => {
    console.log("⭐ Setting primary photo:", {propertyId, photoId});

    try {
      const result = await photoService.setPrimaryPhoto(photoId);
      console.log("✅ Primary photo set successfully");
      return result;
    } catch (error) {
      console.error("❌ Set primary failed:", error);
      throw error;
    }
  };

  /**
   * Reorder photos for a property
   */
  const handleReorderPhotos = async (
    propertyId: number,
    photoIds: number[]
  ) => {
    console.log("🔄 Reordering photos:", {propertyId, photoIds});

    try {
      const result = await photoService.reorderPhotos(propertyId, photoIds);
      console.log("✅ Photos reordered successfully");
      return result;
    } catch (error) {
      console.error("❌ Reorder failed:", error);
      throw error;
    }
  };

  /**
   * Get property photos
   */
  const getPropertyPhotos = async (propertyId: number) => {
    console.log("📥 Getting property photos:", propertyId);

    try {
      const result = await photoService.getPropertyPhotos(propertyId);
      console.log("✅ Photos retrieved successfully");
      return result;
    } catch (error) {
      console.error("❌ Get photos failed:", error);
      throw error;
    }
  };
  return {
    // Data
    properties,
    pagination,
    stats,

    // Loading states
    isLoading: isLoading || isFetching,
    isCreating: isCreating || isCreatingWithPhotos,
    isUpdating,
    isDeleting,
    isBulkUpdating,
    isBulkDeleting,
    statsLoading,

    // Error
    error: fetchError,

    // Actions
    refetch,
    handleCreate,
    handleCreateWithPhotos,
    handleUpdate,
    handleDelete,
    handleBulkUpdate,
    handleBulkDelete,
    handleStatusChange,

    // Photo actions
    handleUploadPhotos,
    handleDeletePhoto,
    handleSetPrimaryPhoto,
    handleReorderPhotos,
    getPropertyPhotos,
  };
};

// Hook for single property
export const useProperty = (id: number | undefined) => {
  const {
    data: property,
    isLoading,
    error,
    refetch,
  } = useGetPropertyByIdQuery(id!, {
    skip: !id,
  });

  return {
    property,
    isLoading,
    error,
    refetch,
  };
};
